import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { StepShell } from './ProgramStepCards';
import { shareToCircle, trackProgramEvent } from '../../lib/programsApi';

export const ProgramCommunityShare = ({ program, day, journalText, locked }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(null);
  const circleId = day.circleIdResolved;
  const circleName = day.circleNameResolved || program.defaultCircleName || 'the Circle';

  const post = async (anonymous) => {
    if (!text.trim()) return toast.error('Write a few words first');
    setBusy(true);
    try {
      await shareToCircle(text.trim(), circleId, anonymous);
      trackProgramEvent(program.slug, 'program_share', { day: day.dayNumber, anonymous });
      setPosted({ anonymous });
      setOpen(false);
      toast.success(anonymous ? 'Shared anonymously with the Circle' : 'Shared with the Circle');
    } catch (e) {
      toast.error(e.message || 'Could not share right now');
    } finally { setBusy(false); }
  };

  const visitCircle = () => {
    trackProgramEvent(program.slug, 'program_circle_click', { day: day.dayNumber });
    navigate(`/circles/${circleId}`);
  };

  return (
    <StepShell icon={Users} label="Community · Optional" testId="step-community">
      <p className="text-base text-[#0f1e33] leading-snug mb-2 font-medium" data-testid="step-community-prompt">{day.circlePrompt}</p>
      <p className="text-[11px] text-[#7a9b8e] mb-4">Sharing is always your choice. Nothing you journal is posted unless you choose to share it.</p>

      {posted ? (
        <div className="rounded-2xl bg-[#f9f4ea] border border-[#7a9b8e]/30 p-3 mb-3" data-testid="step-community-posted">
          <p className="text-xs font-semibold text-[#5c8477]">✓ Shared {posted.anonymous ? 'anonymously ' : ''}with {circleName}</p>
        </div>
      ) : !open ? (
        <button type="button" onClick={() => setOpen(true)} disabled={locked} data-testid="step-community-share-open"
          className="w-full h-11 rounded-full border border-[#0f1e33]/20 text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.18em] hover:border-[#D6AD60] transition-colors disabled:opacity-50 mb-2">
          Share with the Circle
        </button>
      ) : (
        <div className="flex flex-col gap-2 mb-2" data-testid="step-community-composer">
          <p className="text-[11px] text-[#7a9b8e]">Posting to: <span className="font-semibold text-[#1e3a5f]" data-testid="step-community-circle-name">{circleName}</span></p>
          {journalText && !text && (
            <button type="button" onClick={() => setText(journalText)} className="text-left text-[11px] text-[#a88a4c] underline underline-offset-2" data-testid="step-community-use-journal">Use my journal reflection</button>
          )}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="What would you like to share?"
            className="w-full rounded-2xl border border-[#e3d2a8]/60 bg-white/70 p-3 text-sm text-[#0f1e33] focus:outline-none focus:border-[#D6AD60] resize-none" data-testid="step-community-textarea" />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => post(false)} disabled={busy} data-testid="step-community-post-named"
              className="h-11 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.15em] disabled:opacity-50">Post with my name</button>
            {day.allowAnonymousCommunityPost !== false && (
              <button type="button" onClick={() => post(true)} disabled={busy} data-testid="step-community-post-anon"
                className="h-11 rounded-full bg-[#7a9b8e] text-white text-xs font-semibold uppercase tracking-[0.15em] disabled:opacity-50">Post anonymously</button>
            )}
          </div>
          <button type="button" onClick={() => setOpen(false)} className="text-xs text-[#7a9b8e] py-1" data-testid="step-community-cancel">Cancel</button>
        </div>
      )}
      <button type="button" onClick={visitCircle} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1e3a5f]" data-testid="step-community-visit-circle">
        Visit {circleName} <ChevronRight className="w-3.5 h-3.5 text-[#D6AD60]" />
      </button>
    </StepShell>
  );
};
